import React, {useCallback, useEffect, useRef} from "react";
import {Category, Menu, Product} from "@/data/entities/menu";
import {SetStateFn} from "@/utils/SetStateUtils";
import {ActionRow, ActionRowButton, Modal, useNotifications} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {FormProvider, useForm} from "react-hook-form";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {api} from "@/data/api";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {ADMIN_MENU_QUERY_KEY, ProductEditValues} from "@/data/entities/menuEditor";
import EditProductForm from "@widgets/menuEditor/menu/product/EditProductForm";
import {toProductFormValues, toProductRequest} from "@widgets/menuEditor/menu/product/productFormValues";

export type EditProductModalConfig = {
    show: boolean,
    product: Product | null, // `null` for a new product
};

type Props = {
    config: EditProductModalConfig,
    setConfig: SetStateFn<EditProductModalConfig>,
    menu: Menu,
    category: Category, // A new product is added to this category
};

// Images can't be edited yet: the backend leaves them as they are.

const EditProductModal = ({config: {show, product}, setConfig, menu, category}: Props) => {
    const intl = useIntl();
    const {dashboardLocation} = useRestaurant();
    const {notify} = useNotifications();
    const queryClient = useQueryClient();
    const formMethods = useForm<ProductEditValues>({
        defaultValues: toProductFormValues(null, menu, category),
    });
    const {reset} = formMethods;
    const wasShown = useRef(false);
    const onClose = useCallback(() => setConfig(prev => ({...prev, show: false})), [setConfig]);

    // Fill the form when the modal opens (not on a refetch of the menu while
    // it's open: that would undo the changes being made)
    useEffect(() => {
        if (show && !wasShown.current) {
            reset(toProductFormValues(product, menu, category));
        }
        wasShown.current = show;
    }, [show, product, menu, category, reset]);

    const {mutate: upsertProduct, isPending} = useMutation({
        mutationFn: async (form: ProductEditValues): Promise<Product> => {
            const request: ProductEditValues = toProductRequest(form);
            return product === null
                ? api.menuEditor.createProduct(dashboardLocation.id, request)
                : api.menuEditor.updateProduct(dashboardLocation.id, product.id, request);
        },
        onSuccess: async () => {
            // Refetch: a change can also affect categories (e.g. a new product)
            await queryClient.invalidateQueries({queryKey: [ADMIN_MENU_QUERY_KEY, dashboardLocation.id]});
            onClose();
            notifySuccess(intl.formatMessage({id: "saved"}), notify);
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
    });
    const handleSubmitHandler: (() => void) = formMethods.handleSubmit((data: ProductEditValues) => {
        upsertProduct(data);
    });

    return (
        <Modal
            show={show}
            onClose={onClose}
            title={intl.formatMessage({id: product === null ? "newDish" : "editDish"})}
            maxWidth={"max-w-2xl"}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"secondary"}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    onClose();
                                }
                            },
                        }}
                    >
                        <FormattedMessage id={"cancel"}/>
                    </ActionRowButton>

                    <ActionRowButton
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    handleSubmitHandler();
                                }
                            },
                        }}
                    >
                        <FormattedMessage id={"saveAndClose"}/>
                    </ActionRowButton>
                </ActionRow>
            }
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <FormProvider {...formMethods}>
                <EditProductForm
                    product={product}
                    menu={menu}
                    handleSubmitHandler={handleSubmitHandler}
                    disabledState={isPending}
                />
            </FormProvider>

            <small className={"w-full text-right inline-block"}>
                <FormattedMessage id={"updateProductCustomerSiteInfo"}/>
            </small>
        </Modal>
    );
};

export default EditProductModal;